import { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';

function createTimeBlocks(): SelectOption[] {
    const textHours = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
    const amValueHours = ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
    const pmValueHours = ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'];
    const minutes = ['00', '15', '30', '45'];
    const amSelectOptions = [];
    const pmSelectOptions = [];

    for (let i = 0; i < textHours.length; i++) {
        for (let j = 0; j < minutes.length; j++) {
            const baseText = `${textHours[i]}:${minutes[j]}`;
            amSelectOptions.push({
                label: `${baseText}am`,
                value: `${amValueHours[i]}:${minutes[j]}:00`,
            });
            pmSelectOptions.push({
                label: `${baseText}pm`,
                value: `${pmValueHours[i]}:${minutes[j]}:00`,
            });
        }
    }
    return [...amSelectOptions, ...pmSelectOptions];
}

export const timeBlocks = createTimeBlocks();

// Set from which year users are allow to select graph data.
export const startYear = 2019;

// Tickets aren't a rental type, but the frozen daily report tags ticket rows with this
// value so they filter like one. Gated on ParkingSpot.has_ticket_revenue everywhere.
export const TICKET_RENTAL_TYPE = 'ticket';

export const rentalTypesForReports = {
    '': 'View all',
    transient: 'Transient rentals',
    monthly: 'Monthly rentals',
    bulk_rental: 'Tenant rentals',
    enterprise: 'Enterprise rentals',
    hourly: 'Hourly rentals',
    'flat rate': 'Flat rate rentals',
    daily: 'Daily rentals',
    hourly_and_monthly: 'Hourly + monthly rentals',
    // Tickets aren't a rental type, but the frozen report backing this page's daily
    // totals tags ticket rows with rental_type='ticket', so it filters the same way.
    ticket: 'Violations',
} as const;

export type RentalTypeFilterValue = keyof typeof rentalTypesForReports;

export const reportRentalTypeOptionsForSelect: SelectOption[] = Object.entries(rentalTypesForReports).map(
    ([value, label]) => ({
        value,
        label,
    })
);

// The "Violations" filter is only meaningful for spots that share ticket revenue with the
// owner; everywhere else it would advertise a revenue stream the owner has no claim to.
// Defaults to hiding it so a not-yet-loaded flag never leaks the option.
export function getReportRentalTypeOptions(hasTicketRevenue?: boolean): SelectOption[] {
    if (hasTicketRevenue) return reportRentalTypeOptionsForSelect;
    return reportRentalTypeOptionsForSelect.filter((option) => option.value !== TICKET_RENTAL_TYPE);
}

export enum VisitorValidationSource {
    DASHBOARD = 'dashboard',
    KIOSK = 'kiosk',
}

export enum AffiliationRole {
    ADMIN = 'Admin',
    ENFORCER = 'Enforcer',
    EMPLOYEE = 'Employee',
}

export enum RentType {
    monthly = 'monthly',
    enterprise = 'enterprise',
    daily = 'daily',
    hourly = 'hourly',
    flatRate = 'flat rate',
    freeUntilHourly = 'free_until_hourly',
}

export enum PricingStrategy {
    SURGE_PRICING = 'surge_pricing',
    STANDARD = 'standard',
    EVENT = 'event',
    RATE = 'rate',
}

export enum RateIntervalOption {
    ONE_TIME = 'one_time',
    DAILY = 'daily',
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
    ALWAYS_ON = 'always_on',
}

export enum RentalType {
    Daily = 'daily',
    Monthly = 'monthly',
    Hourly = 'hourly',
    Flat = 'flat rate',
    FreeUntil = 'free_until_hourly',
    Enterprise = 'enterprise',
    BulkRental = 'bulk_rental',
}

export enum PricingModelsOptions {
    LINEAR = 'linear',
    STEPWISE = 'stepwise',
    EXPONENTIAL = 'exponential',
    MOVING = 'moving',
    MOVING_TOTAL = 'moving_total',
    CUMULATIVE_TIME = 'cumulative_time',
    STATIC = 'static',
}

export enum SlotState {
    Active = 'ACTIVE',
    Upcoming = 'UPCOMING',
    Ended = 'ENDED',
}

export const getDayFnToDayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const monthCompleteName: Record<string, string> = {
    Jan: 'January',
    Feb: 'February',
    Mar: 'March',
    Apr: 'April',
    May: 'May',
    Jun: 'June',
    Jul: 'July',
    Aug: 'August',
    Sep: 'September',
    Oct: 'October',
    Nov: 'November',
    Dec: 'December',
};

export const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export enum InvoiceStatus {
    DRAFT = 'draft',
    OPEN = 'open',
    PAID = 'paid',
    VOID = 'void',
    UNCOLLECTIBLE = 'uncollectible',
    PAST_DUE = 'past due',
    NA = 'n/a',
}

export const anonymousUsername = 'anonymous@user.com';

export const EFEKTA_ORG_PK = 711;

export const RETOOL_SPOT_FORM_URL =
    'https://airgarage.retool.com/apps/54a1424a-2476-11f0-a4f2-8783caf0127f/Create%20Parking%20Spot%20Form%20/parking-lot-creator';

export type TenantNameSort = 'asc' | 'desc';
